import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from 'react-apollo-hooks'
import styled from 'react-emotion'
import getText from '../strings'
import { colors } from '../../styles'
import { Svg, ga, ProgressiveImage, ProgressiveBackground } from '../../application'
import * as Queries from '../queries'
import * as Mutations from '../mutations'

const Container = styled('div')`
  display: flex;
  height: 400px;

  @media (max-width: 767px) {
    padding: 10px;
  } 
`
const Header = styled('div')`
  display: flex;
  justify-content: space-between;
  .pictogram {
    cursor: pointer;
  }  
`

const Image = styled('div')`  
  width: 33%;
  height: 100%;

  @media (max-width: 767px) {
    display: none;
  } 
`
const Content = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 67%;
  height: 400px;

  @media (max-width: 767px) {
    padding: 0;
    width: 100%;
  } 
`

const Right = styled('div')`
  display:flex;
  overflow: hidden;
  width: 100%;
  justify-content: space-between;
  border-width: 1px;
  border-style: solid;
  border-color: ${props => props.isInCollection ? colors.tui_blue_25 : colors.tui_border};
  border-left: none;
  border-radius: 0 6px 6px 0;
  h4 {
    margin-bottom: 0;
  }
`

const ItemContainer = styled('div')`  
  height:300px; 
  overflow-y: auto; 

  @media (max-width: 767px) {
    height:260px; 
    padding: 10px 0;
  } 
`

const ScrollContainer = styled('div')`  
  max-height:301px;
  div:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 767px) {
    max-height:261px;
    padding: 10px 0;
  } 
`

const CollectionItem = styled('div')`
  display: flex;
  justify-content: space-between;
  border-radius: 6px;
  height: 60px;
  width: 97%;
  margin-bottom: 10px;
  cursor: pointer;  
  background-color: ${props => props.isInCollection ? colors.tui_blue_25 : colors.tui_white};  
  span {
    align-self: center;
    margin-left: 20px;
  }
  img {
    height: 100%;
    width: 90px;
    overflow: hidden;
    border-radius: 6px 0 0 6px;
  }
  input {
    cursor: pointer;
    margin: 14px 14px;
  }
`

const SvgContainer = styled('div')`  
  display: flex;
  justify-content: center;
  flex-grow: 1;
  background-color: ${props => props.backgroundColor};
  width: 90px;
  border: ${props => props.border ? `1px solid ${colors.tui_border}` : 'inherit'};
  border-radius: 6px 0 0 6px;
  svg.pictogram {
    color: ${props => props.color || 'inherit'};
    height: 30px;
    width: 30px;
  }
`

const Center = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: Center;
  align-items: Center;
`

export default ({ hotel, onClose }) => {
  const [collections, setCollections] = useState(null)
  const [isFavourite, setIsFavourite] = useState(null)

  const { loading, data } = useQuery(Queries.getFavouriteHotel(hotel.id), { fetchPolicy: 'no-cache' })

  useEffect(() => {
    if (!loading && data) {
      setCollections(data.ownCollections
        .concat(data.memberCollections)
        .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
        .map(c => ({ ...c, isSelected: data.favouriteHotel.collections.filter(p => p === c.id).length > 0 }))
      )

      setIsFavourite(data.favouriteHotel.isFavourite)
    }
  }, [data])

  const addFavouriteHotel = useMutation(Mutations.addFavouriteHotel)
  const deleteFavouriteHotel = useMutation(Mutations.deleteFavouriteHotel)
  const addFavouriteHotelToCollection = useMutation(Mutations.addFavouriteHotelToCollection)
  const deleteFavouriteHotelFromCollection = useMutation(Mutations.deleteFavouriteHotelFromCollection)

  const handleToggleSelectedInUnsortedCollection = (e) => {
    e.stopPropagation()

    const options = { variables: { hotelId: hotel.id } }
    if (isFavourite)
      deleteFavouriteHotel(options)
    else
      addFavouriteHotel(options)

    setIsFavourite(!isFavourite)
  }

  const handleToggleSelectedInCollection = (e, collection) => {
    e.stopPropagation()

    const options = { variables: { hotelId: hotel.id, collectionId: collection.id } }
    if (collection.isSelected)
      deleteFavouriteHotelFromCollection(options)
    else
      addFavouriteHotelToCollection(options)

    setCollections(collections.map(c => {
      if (c.id === collection.id) {
        c.isSelected = !c.isSelected
      }
      return c
    }))
  }

  const handleClose = () => {
    ga.buttonClick('collections-managecollections-close')
    onClose()
  }

  const createFavouritesCollectionItem = () => {
    return (
      <CollectionItem key='Favourites' isInCollection={isFavourite} onClick={handleToggleSelectedInUnsortedCollection}>
        <SvgContainer backgroundColor={colors.tui_blue_100} color={colors.tui_white}>
          <Center>
            <Svg icon="heart" />
          </Center>
        </SvgContainer>
        <Right isInCollection={isFavourite}>
          <span><h4>{getText('unsortedCollection', 'title')}</h4></span>
          <input type="checkbox" onClick={handleToggleSelectedInUnsortedCollection} onChange={() => { }} checked={isFavourite}></input>
        </Right>
      </CollectionItem>
    )
  }

  const createCollectionItem = (collection) => {
    return (
      <CollectionItem key={collection.id} isInCollection={collection.isSelected} onClick={(e) => handleToggleSelectedInCollection(e, collection)}>
        {collection.image && <ProgressiveImage src={collection.image} imageParams={'w=300'} alt={collection.name} className="ratio-16-9" />}
        {!collection.image &&
          <SvgContainer backgroundColor={colors.tui_white} border>
            <Center>
              <Svg icon="heart" />
            </Center>
          </SvgContainer>
        }
        <Right isInCollection={collection.isSelected}>
          <span><h4>{collection.name}</h4></span>
          <input type="checkbox" onClick={(e) => handleToggleSelectedInCollection(e, collection)} onChange={() => { }} checked={collection.isSelected}></input>
        </Right>
      </CollectionItem>
    )
  }

  if (!collections)
    return null

  return (
    <Container>
      <Image>
        <ProgressiveBackground src={data.favouriteHotel.image} imageParams={"w=640"} />
      </Image>
      <Content>
        <Header>
          <div>
            <h3>{hotel.name}</h3>
            <h5>{getText('manageCollections', 'description')}</h5>
          </div>
          <Svg icon='times' onClick={handleClose} />
        </Header>
        <ItemContainer>
          <ScrollContainer>
            {createFavouritesCollectionItem()}
            {collections.map(c => createCollectionItem(c))}
          </ScrollContainer>
        </ItemContainer>
      </Content>
    </Container>
  )
}